import { toRaw } from 'vue'
import {
  useDeviceManagementPrinterStatusStore,
  type PrinterStatus
} from '@nsftx/seven-client-retail-vue-dm'
import { logService } from '@nsftx/seven-client-retail-utils'
import { useNotificationsStore, type RetailNotification } from '@nsftx/seven-nxg-retail-vue-sdk'
import i18n from '@/plugins/translationsPlugin'
import type { PrinterPaperStatusChangeAction } from '@/modules/print/printerTypes'
import { handleAcceptorsStateOnPrinterStatusChange } from '@/modules/acceptors/acceptorsService'
import { useCmsConfigStore } from '@/stores/cms/cmsConfigStore'

const LOG_PREFIX = '[printerStatusService]'
const PRINTER_STATUS_ERROR_NOTIFICATION_ID = 'printer_status_error'
const PRINTER_STATUS_WARNING_NOTIFICATION_ID = 'printer_status_warning'

const { t } = i18n.global

const getPaperLowEffectsConfig = () => {
  const appSettings = useCmsConfigStore().cmsConfig?.appSettings

  return {
    areAcceptorsDisabledOnPaperLow: appSettings?.areAcceptorsDisabledOnPaperLow ?? false,
    isSlipPayinDisabledOnPaperLow: appSettings?.isSlipPayinDisabledOnPaperLow ?? false
  }
}

const handlePrinterStatusWarningNotification = () => {
  const { printerStatus } = useDeviceManagementPrinterStatusStore()
  const { t } = i18n.global
  const notificationsStore = useNotificationsStore()
  const showWarningsConfig = true
  const shouldShowWarningNotification =
    showWarningsConfig && printerStatus.code !== undefined && printerStatus.message

  if (shouldShowWarningNotification) {
    notificationsStore.showNotification({
      id: PRINTER_STATUS_WARNING_NOTIFICATION_ID,
      variant: 'warning',
      text: [t(printerStatus.message)],
      timeout: 3000
    })
  }
}

const getPaperLowStatusNotificationMessage = () => {
  let translationKey = ''
  const { areAcceptorsDisabledOnPaperLow, isSlipPayinDisabledOnPaperLow } =
    getPaperLowEffectsConfig()

  if (areAcceptorsDisabledOnPaperLow && isSlipPayinDisabledOnPaperLow) {
    translationKey = 'printer_status_paper_low_acceptors_ticket_payin_disabled'
  } else if (areAcceptorsDisabledOnPaperLow) {
    translationKey = 'printer_status_paper_low_acceptors_disabled'
  } else if (isSlipPayinDisabledOnPaperLow) {
    translationKey = 'printer_status_paper_low_ticket_payin_disabled'
  }

  return t(translationKey)
}

const handlePaperLowNotificationBasedOnConfig = () => {
  const { areAcceptorsDisabledOnPaperLow, isSlipPayinDisabledOnPaperLow } =
    getPaperLowEffectsConfig()

  if (!areAcceptorsDisabledOnPaperLow && !isSlipPayinDisabledOnPaperLow) {
    return
  }

  const notificationsStore = useNotificationsStore()
  const { isStatusPaperLow } = useDeviceManagementPrinterStatusStore()

  if (isStatusPaperLow()) {
    notificationsStore.closeNotificationById('printer_status_paper_low')
    notificationsStore.showNotification({
      id: 'printer_status_paper_low',
      text: [getPaperLowStatusNotificationMessage()],
      variant: 'error',
      timeout: null
    })
  } else {
    notificationsStore.closeNotificationById('printer_status_paper_low')
  }
}

const onPaperLowStatusChange = () => {
  const { isStatusPaperLow } = useDeviceManagementPrinterStatusStore()
  const { areAcceptorsDisabledOnPaperLow } = getPaperLowEffectsConfig()

  if (!areAcceptorsDisabledOnPaperLow) {
    return
  }

  const shouldStopAcceptorsOnPaperLow = areAcceptorsDisabledOnPaperLow && isStatusPaperLow()

  handleAcceptorsStateOnPrinterStatusChange(
    shouldStopAcceptorsOnPaperLow,
    'PaperLow',
    'acceptors-start-error-paper-low'
  )
}

const onPaperOutStatusChange = () => {
  const { isStatusPaperOut } = useDeviceManagementPrinterStatusStore()

  handleAcceptorsStateOnPrinterStatusChange(
    isStatusPaperOut(),
    'PaperOut',
    'acceptors-start-error-paper-out'
  )
}

const onOfflineStatusChange = () => {
  const { isStatusOffline } = useDeviceManagementPrinterStatusStore()

  handleAcceptorsStateOnPrinterStatusChange(
    isStatusOffline(),
    'Offline',
    'acceptors-start-error-printer-offline'
  )
}

const showPrinterStatusErrorNotification = () => {
  const { printerStatus, isStatusPaperOut } = useDeviceManagementPrinterStatusStore()
  const notificationsStore = useNotificationsStore()
  const { t } = i18n.global

  const paperOutNonClosableEnabled = true
  const printerStatusMessage = printerStatus.message ? `${t(printerStatus.message)}.` : ''
  const errorMessage = `${printerStatusMessage} ${t('printer_money_actions_blocked')}`
  const isPaperOutNonClosable = paperOutNonClosableEnabled && isStatusPaperOut()
  const errorNotification: RetailNotification = {
    id: PRINTER_STATUS_ERROR_NOTIFICATION_ID,
    variant: 'error',
    closable: !isPaperOutNonClosable,
    text: [isPaperOutNonClosable ? t('printer_paper_error_notification') : errorMessage],
    timeout: null
  }

  notificationsStore.showNotification(errorNotification)
}

const handlePrinterStatusNotification = () => {
  const notificationsStore = useNotificationsStore()
  const { isPrinterValid } = useDeviceManagementPrinterStatusStore()

  notificationsStore.closeNotificationById(PRINTER_STATUS_ERROR_NOTIFICATION_ID)
  notificationsStore.closeNotificationById(PRINTER_STATUS_WARNING_NOTIFICATION_ID)

  if (isPrinterValid()) {
    handlePrinterStatusWarningNotification()
    handlePaperLowNotificationBasedOnConfig()
  } else {
    showPrinterStatusErrorNotification()
  }
}

const handlePrinterStatusAction = (
  newPrinterStatus: PrinterStatus,
  oldPrinterStatus: PrinterStatus
) => {
  const changedPrinterStatusAction = getChangedPrinterStatusAction(
    newPrinterStatus,
    oldPrinterStatus
  )

  logService.info(`${LOG_PREFIX} Printer status is changed, try to call action handler`, {
    code: 'ST_PRINTER_STATUS_CHANGE_ACTION',
    details: {
      oldPrinterStatus: toRaw(oldPrinterStatus),
      newPrinterStatus: toRaw(newPrinterStatus),
      changedPrinterStatusAction
    }
  })

  switch (changedPrinterStatusAction) {
    case 'PaperLow':
      onPaperLowStatusChange()
      break

    case 'PaperOut':
      onPaperOutStatusChange()
      break

    case 'Offline':
      onOfflineStatusChange()
      break

    default:
      break
  }
}

const handlePrinterStatusChange = (
  newPrinterStatus: PrinterStatus,
  oldPrinterStatus: PrinterStatus
) => {
  handlePrinterStatusNotification()
  handlePrinterStatusAction(newPrinterStatus, oldPrinterStatus)
}

const getChangedPrinterStatusAction = (
  newPrinterStatus: PrinterStatus,
  oldPrinterStatus: PrinterStatus
): PrinterPaperStatusChangeAction | undefined => {
  if (
    newPrinterStatus.valid !== oldPrinterStatus.valid &&
    !newPrinterStatus.paperLow &&
    !newPrinterStatus.paperOut
  ) {
    return 'Offline'
  }

  if (newPrinterStatus.paperLow !== oldPrinterStatus.paperLow) {
    return 'PaperLow'
  }

  if (newPrinterStatus.paperOut !== oldPrinterStatus.paperOut) {
    return 'PaperOut'
  }

  return undefined
}

export {
  handlePrinterStatusChange,
  showPrinterStatusErrorNotification,
  PRINTER_STATUS_ERROR_NOTIFICATION_ID,
  PRINTER_STATUS_WARNING_NOTIFICATION_ID
}
